import React from "react";
import AccessRuleRenderer from "./AccessRuleRenderer";
import LinkAnchor from "../components/LinkAnchor";
import ScrollContainer from "./ScrollContainer";
import SubjectRenderer from "./SubjectRenderer";
import localize from "../utils/LocalizationStore";
import QuestionnaireAccessTypeSelector, { accessTypeValueToAccessLevelState as accessTypeFunc } from "./QuestionnaireAccessTypeSelector";
import { IQuestionnaireInstance, Questionnaire, UpdateQuestionnaireAccessPolicy } from "../utils/ServiceManagerAPI";
import { useUser } from "../utils/AccountStore";
import { FcEmptyTrash } from "react-icons/fc";
import "../main.css";

const trash = <FcEmptyTrash size={20} />;

interface QuestionnaireAccessProps {
    questionnaire: Questionnaire;
    reviews: IQuestionnaireInstance[];
    onChange: () => void;
}

const QuestionnaireAccess = ({ questionnaire, onChange, reviews }: QuestionnaireAccessProps) => {
    const { user } = useUser();

    const removeAccessPolicy = (questionnaireID, rule) => () => {
        UpdateQuestionnaireAccessPolicy({ questionnaireID, rule, accessType: 0 }).then(() => {
            onChange();
        });
    };

    return (
        <>
            <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">Share</h3>
            <ScrollContainer direction={"-y"}>
                <div className="w-full max-w-4xl">
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">Share Questionnaire</h4>
                    {user.hasAnyQuestionnaireAccess(questionnaire.id, 64) && (
                        <table className="w-full border-collapse">
                            <thead>
                                <tr className="border-b border-gray-300 dark:border-gray-600">
                                    <th className="text-left p-2 text-gray-900 dark:text-gray-100 font-semibold">Access Rule</th>
                                    <th className="text-left p-2 text-gray-900 dark:text-gray-100 font-semibold">Permissions</th>
                                    <th className="text-left p-2 text-gray-900 dark:text-gray-100 font-semibold"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {questionnaire.accessPolicies &&
                                    questionnaire.accessPolicies.map((accessPolicy, key) => (
                                        <tr key={key} className="border-b border-gray-200 dark:border-gray-700">
                                            <td className="p-2">
                                                <AccessRuleRenderer rule={accessPolicy.rule}></AccessRuleRenderer>
                                            </td>
                                            <td className="p-2">
                                                <QuestionnaireAccessTypeSelector value={accessTypeFunc(accessPolicy.accessType)} readonly></QuestionnaireAccessTypeSelector>
                                            </td>
                                            <td className="p-2">
                                                <div className="flex flex-row gap-2 items-center">
                                                    <LinkAnchor path={`$update/${accessPolicy.rule}`} label="Edit"></LinkAnchor>
                                                    <button 
                                                        className="p-1 text-gray-500 hover:text-red-500 transition-colors"
                                                        onClick={removeAccessPolicy(accessPolicy.questionnaireID, accessPolicy.rule)}
                                                    >
                                                        {trash}
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                <tr>
                                    <td className="p-2">
                                        <LinkAnchor path={`$create`} label="Create New Access Rule"></LinkAnchor>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    )}
                    {!user.hasRole("Admin") && <span className="text-gray-500 dark:text-gray-400">You do not have permission to share.</span>}
                </div>
            </ScrollContainer>
        </>
    );
};

export default QuestionnaireAccess;
